import React from 'react'
import { Group, IconButton, iconSizes, Text } from '@common/ui'
import { IconGripVertical, IconTrash } from '@common/ui/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { com } from '@eidu/web'
import FieldData from '../../domain/entity/FieldData'
import FieldInput from '../../components/input/FieldInput'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import FieldType = com.eidu.entity.field.FieldType
import TypeValidationError = com.eidu.entity.validation.TypeValidationError

type SortableFieldRowProps = {
  id: string
  index: number
  fieldData: FieldData
  availableEntityTypes: readonly EntityType[]
  submitting: boolean
  errors?: readonly TypeValidationError[]
  onRemove: () => void
  onFieldTypeChange: (typeName: string) => Promise<void>
  onRequiredChange: () => Promise<void>
  onReferenceTypesChange: (types: readonly EntityTypeId[]) => Promise<void>
  onFieldNameChange: (name: string) => Promise<void>
}

const SortableFieldRow = ({
  id,
  index,
  fieldData,
  availableEntityTypes,
  submitting,
  errors,
  onRemove,
  onFieldTypeChange,
  onRequiredChange,
  onReferenceTypesChange,
  onFieldNameChange,
}: SortableFieldRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    border: '1px solid var(--trellis-color-outline)',
    borderRadius: 'var(--trellis-radius-sm)',
    padding: 'var(--trellis-spacing-md)',
    backgroundColor: 'var(--trellis-color-surface)',
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Group gap="md" justify="space-between">
        <Group gap="xs" style={{ flexGrow: 1 }}>
          <IconButton
            size="sm"
            disabled={submitting}
            aria-label="Reorder field"
            style={{ cursor: submitting ? 'not-allowed' : 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={iconSizes.sm} stroke={1.5} />
          </IconButton>
          <Text fw={500}>{fieldData.field.name || `Field ${index + 1}`}</Text>
        </Group>
        <IconButton color="negative" onClick={onRemove} size="sm" disabled={submitting} aria-label="Remove field">
          <IconTrash size={iconSizes.sm} stroke={1.5} />
        </IconButton>
      </Group>
      <div style={{ marginTop: 'var(--trellis-spacing-md)' }}>
        <FieldInput
          availableEntityTypes={availableEntityTypes}
          disabled={submitting}
          editable={!fieldData.existing}
          fieldType={fieldData.field.type}
          handleFieldTypeChange={(value) => onFieldTypeChange(value)}
          handleRequiredChange={onRequiredChange}
          handleReferenceTypesChange={onReferenceTypesChange}
          handleFieldNameChange={onFieldNameChange}
          idPrefix={`manage-entity-type-field-${index}`}
          name={fieldData.field.name}
          referenceTypes={
            fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => Array.from(set)) || []
          }
          referenceTypesError={
            fieldData.field.type === FieldType.Reference
              ? !fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => set.size > 0)
              : false
          }
          required={fieldData.field.required}
          updating={false}
          errors={errors}
          editableName
        />
      </div>
    </div>
  )
}

export default SortableFieldRow
